'use client'
import {Suspense, useDeferredValue, useEffect, useMemo, useState} from 'react';
import styles from './Projects.module.css'; // Import the new CSS module
import {Project, ProjectRow} from "@/shared/pages/projectsShared";
import {ProjectCard} from "@/components/sections/ProjectCard";
import {consts} from "@/shared/commonShared";
import {useRouter, useSearchParams} from "next/navigation";
import ExportedImage from "next-image-export-optimizer";

function ProjectsList({ projects }: { projects: Project[] }) {
	// --- State Management ---
	const router = useRouter();
	const searchParams = useSearchParams();
	// We default to 'All Projects' if no param exists
	const [activeCategory, setActiveCategory] = useState<string>('All Projects');

	const currentParamsString = searchParams.toString();
	const preserveParams = currentParamsString ? `?${currentParamsString}` : "";

	// 2. Sync URL -> State
	useEffect(() => {
		// Read ?category=game from URL
		const categoryParam = searchParams.get("category");
		if (categoryParam) {
			// Convert "game" back to ".game" for your internal filtering logic
			setActiveCategory(`${categoryParam}`);
		} else {
			setActiveCategory("All Projects");
		}
	}, [searchParams]);

	// 3. Handle Click (Update URL)
	const handleFilterClick = (filterKey: string) => {
		// Convert ".game" -> "game" for the URL
		const urlValue = filterKey.replace(".", "");

		if (filterKey === "All Projects") {
			router.push("/projects", { scroll: false });
		} else {
			router.push(`/projects?category=${urlValue}`, { scroll: false });
		}
	};

	const deferredFilter = useDeferredValue(activeCategory);

	// --- Data Fetching ---
	const allFilters = useMemo(() => {
		const filtersSet = new Set<string>();
		projects.forEach(p => {
			p.flags.forEach(f => filtersSet.add(f));
		});
		return ['All Projects', ...Array.from(filtersSet).sort()];
	}, [projects]);

	const visibleProjects = useMemo(() => {
		if (deferredFilter === "All Projects") {
			return projects;
		}
		return projects.filter(project => {
			return project.flags.includes(deferredFilter);
		});
	}, [projects, deferredFilter]);

	const sortedVisibleProjects = useMemo(() => {
		return visibleProjects.slice().sort(
			(a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
		);
	}, [visibleProjects]);

	const projectRows = useMemo<ProjectRow[]>(() => {
		if (visibleProjects.length === 0) return [];

		const fQueue = visibleProjects.filter(p => p.featured);
		const nQueue = visibleProjects.filter(p => !p.featured);
		const newRows: ProjectRow[] = [];

		let lastPattern: ProjectRow["type"] = "NNF";
		let rowId = 0;

		while (fQueue.length > 0 || nQueue.length > 0) {
			rowId++;

			if (lastPattern === "NNF" && fQueue.length >= 1 && nQueue.length >= 2) {
				newRows.push({
					id: `row-${rowId}`,
					type: "FNN",
					items: [fQueue.shift()!, nQueue.shift()!, nQueue.shift()!],
				});
				lastPattern = "FNN";
				continue;
			}

			if (lastPattern === "FNN" && nQueue.length >= 2 && fQueue.length >= 1) {
				newRows.push({
					id: `row-${rowId}`,
					type: "NNF",
					items: [nQueue.shift()!, nQueue.shift()!, fQueue.shift()!],
				});
				lastPattern = "NNF";
				continue;
			}

			if (fQueue.length >= 1 && nQueue.length >= 2) {
				newRows.push({
					id: `row-${rowId}`,
					type: "FNN",
					items: [fQueue.shift()!, nQueue.shift()!, nQueue.shift()!],
				});
				lastPattern = "FNN";
				continue;
			}

			if (nQueue.length >= 2 && fQueue.length >= 1) {
				newRows.push({
					id: `row-${rowId}`,
					type: "NNF",
					items: [nQueue.shift()!, nQueue.shift()!, fQueue.shift()!],
				});
				lastPattern = "NNF";
				continue;
			}

			const remaining = [...fQueue.splice(0), ...nQueue.splice(0)];
			if (remaining.length > 0) {
				newRows.push({
					id: `row-${rowId}`,
					type: "FALLBACK",
					items: remaining,
				});
			}
		}

		return newRows;
	}, [visibleProjects]);

	// --- Helper Functions ---

	// Toggles the 'active' class on filter buttons
	const activeBtn = (filter: string) => {
		return activeCategory === filter ? "active btn btn-md btn-filter mb-2 me-2" : "btn btn-md btn-filter mb-2 me-2";
	};


	// --- Main JSX Render ---
	return (
		<>
			<section className={"section-team-1 position-relative fix py-"+consts.PADDING}>
				<div className="position-relative z-2">
				<div className="container position-relative z-2">
					{/* Section Header */}
					<div className="text-center">
						<div className="d-flex align-items-center justify-content-center bg-primary-soft border border-2 border-white d-inline-flex rounded-pill px-4 py-2">
							<ExportedImage
								src="/assets/imgs/dots.png"
								alt="decorative dots"
								width={24}
								height={24}
							/>
							<span className="tag-spacing fs-7 fw-bold text-linear-2 ms-2 text-uppercase">Our Work</span>
						</div>
						<h3 className={"ds-3 py-"+consts.PADDING_SMALL}>Explore Our Projects</h3>
						<p className="fs-5">
							Here's a selection of our featured work, showcasing our expertise and love for game development...
						</p>
					</div>

					{/* Filter Buttons */}
					{false && <div className={"text-center mt-"+consts.PADDING}>
						<div className="button-group filter-button-group filter-menu-active">
							{allFilters.map(filter => (
								<button
									key={filter}
									// Check against 'activeCategory' state
									className={activeBtn(filter)}
									onClick={() => handleFilterClick(filter)}
								>
									{filter}
								</button>
							))}
						</div>
					</div>}
				</div>


				{/* The Project Grid Mobile */}
				<div className={"container d-md-none mt-"+consts.PADDING}>
					<div className="row">
						{sortedVisibleProjects.map((p, index) => (<ProjectCard key={p.id} project={p} colClass="col-12" isFirst={index === 0} queryParams={preserveParams} />))}
					</div>
				</div>

				{/* The Project Grid - Desktop */}
				<div className={"container d-none d-md-block mt-"+consts.PADDING} aria-hidden="true">
					{/* We map over the 'projectRows' array */}
					{projectRows.map((row) => (
						// Each 'row' object gets its own Bootstrap 'row' div
						<div className="justify-content-start row" key={row.id}>

							{/* Render NNN Pattern (3 normal projects) */}
							{row.type === 'NNN' && (
								<>
									{row.items[0] && (<ProjectCard project={row.items[0]} colClass="col-12 col-md-4" queryParams={preserveParams} />)}
									{row.items[1] && (<ProjectCard project={row.items[1]} colClass="col-12 col-md-4" queryParams={preserveParams} />)}
									{row.items[2] && (<ProjectCard project={row.items[2]} colClass="col-12 col-md-4" queryParams={preserveParams} />)}
								</>
							)}


							{/* Render FNN Pattern (1 Featured, 2 Stacked Normals) */}
							{row.type === 'FNN' && (
								<>
									{/* Left Column: Featured (8 cols) */}
									{row.items[0] && (<ProjectCard project={row.items[0]} colClass="col-12 col-md-8" queryParams={preserveParams} />)}

									{/* Right Column: Normals (4 cols) */}
									<div className="col-12 col-md-4">
										{/* Apply the 'nestedRow' class for 100% height */}
										<div className={`row ${styles.nestedRow}`}>
											{row.items[1] && (<ProjectCard project={row.items[1]} colClass="col-12" queryParams={preserveParams} />)}
											{row.items[2] && (<ProjectCard project={row.items[2]} colClass="col-12" queryParams={preserveParams} />)}
										</div>
									</div>
								</>
							)}

							{/* Render NNF Pattern (2 Stacked Normals, 1 Featured) */}
							{row.type === 'NNF' && (
								<>
									{/* Left Column: Normals (4 cols) */}
									<div className="col-12 col-md-4">
										{/* Apply the 'nestedRow' class for 100% height */}
										<div className={`row ${styles.nestedRow}`}>
											{row.items[0] && (<ProjectCard project={row.items[0]} colClass="col-12" queryParams={preserveParams} />)}
											{row.items[1] && (<ProjectCard project={row.items[1]} colClass="col-12" queryParams={preserveParams} />)}
										</div>
									</div>

									{/* Right Column: Featured (8 cols) */}
									{row.items[2] && (<ProjectCard project={row.items[2]} colClass="col-12 col-md-8" queryParams={preserveParams} />)}
								</>
							)}

							{/* Render FALLBACK (for leftovers) */}
							{row.type === 'FALLBACK' && (
								row.items.map(project => (
									// Just render them as standard 4-col items
									(<ProjectCard key={project.id} project={project} colClass="col-12 col-md-4" queryParams={preserveParams} />)
								))
							)}
						</div>
					))}
				</div>

				</div>
				<div className="bouncing-blobs-container">
					<div className="bouncing-blobs-glass" />
					<div className="bouncing-blobs">
						<div className="bouncing-blob bouncing-blob--green" />
						<div className="bouncing-blob bouncing-blob--primary" />
					</div>
				</div>
			</section>
		</>
	)
}

// wrap in Suspense because we use 'useSearchParams'
export default function Projects({ projects }: { projects: Project[] }) {
	return (
		<Suspense fallback={
			// 1. minHeight: Keeps the footer pushed down (prevents layout shift)
			// 2. bg-transparent: It is invisible to the user
			<section
				className={"section-team-1 position-relative fix py-"+consts.PADDING}
				style={{ minHeight: '1200px' }} // Estimate height of your list
			>
				{/* Optional: You can put a spinner here if you want, or leave empty */}
			</section>
		}>
			<ProjectsList projects={projects} />
		</Suspense>
	)
}